
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import RouteErrorBoundary from '@/components/error/RouteErrorBoundary';
import PageGuard from '@/components/auth/PageGuard';
import Index from '@/pages/Index';
import FlowRateCalculator from '@/pages/FlowRateCalculator';
import PressureAdvanceCalculator from '@/pages/PressureAdvanceCalculator';
import Tools from '@/pages/Tools';
import PartDetails from '@/pages/PartDetails';
import Parts from '@/pages/Parts';
import Tutorials from '@/pages/Tutorials';
import Donate from '@/pages/Donate';
import UnderConstruction from '@/pages/UnderConstruction';
import Auth from '@/pages/Auth';
import AuthCallback from '@/pages/AuthCallback';
import TutorialCreator from '@/pages/TutorialCreator';
import TutorialDetails from '@/pages/TutorialDetails';
import Roadmap from '@/pages/Roadmap';
import Contribute from '@/pages/Contribute';
import SavedItems from '@/pages/SavedItems';
import SuggestFeature from '@/pages/SuggestFeature';
import NotFound from '@/pages/NotFound';
import ProfilePage from '@/pages/ProfilePage';
import PreferencesPage from '@/pages/PreferencesPage';
import AdminRoute from '@/components/auth/AdminRoute';
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminSubmissions from '@/pages/admin/Submissions';
import FilamentManagement from '@/pages/admin/FilamentManagement';
import AdminAnalytics from '@/pages/admin/Analytics';
import ComponentManager from '@/pages/admin/ComponentManager';
import PageAccessControl from '@/pages/admin/PageAccessControl';

const RouteConfig = () => {
  return (
    <Routes>
      {/* Home route - now redirects to roadmap */}
      <Route path="/" element={
        <RouteErrorBoundary routeName="Home">
          <Index />
        </RouteErrorBoundary>
      } />
      
      {/* Calculator routes - all under /calculators/ - Protected by PageGuard */}
      <Route path="/calculators/flow-rate" element={
        <RouteErrorBoundary routeName="Flow Rate Calculator">
          <PageGuard pageName="tools">
            <FlowRateCalculator />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      <Route path="/calculators/pressure-advance" element={
        <RouteErrorBoundary routeName="Pressure Advance Calculator">
          <PageGuard pageName="tools">
            <PressureAdvanceCalculator />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      
      {/* Legacy route redirects for backward compatibility */}
      <Route path="/calibration" element={<Navigate to="/calculators/flow-rate" replace />} />
      
      {/* Main application routes - Protected by PageGuard */}
      <Route path="/tools" element={
        <RouteErrorBoundary routeName="Tools">
          <PageGuard pageName="tools">
            <Tools />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      <Route path="/parts" element={
        <RouteErrorBoundary routeName="Parts">
          <PageGuard pageName="parts">
            <Parts />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      <Route path="/part-details" element={
        <RouteErrorBoundary routeName="Part Details">
          <PageGuard pageName="parts">
            <PartDetails />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      <Route path="/part-details/:id" element={
        <RouteErrorBoundary routeName="Part Details">
          <PageGuard pageName="parts">
            <PartDetails />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      <Route path="/parts/:id" element={
        <RouteErrorBoundary routeName="Part Details">
          <PageGuard pageName="parts">
            <PartDetails />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      <Route path="/tutorials" element={
        <RouteErrorBoundary routeName="Tutorials">
          <PageGuard pageName="guides">
            <Tutorials />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      {/* Support both slug and ID based tutorial routing */}
      <Route path="/tutorials/:identifier" element={
        <RouteErrorBoundary routeName="Tutorial Details">
          <PageGuard pageName="guides">
            <TutorialDetails />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      <Route path="/donate" element={
        <RouteErrorBoundary routeName="Donate">
          <PageGuard pageName="donate">
            <Donate />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      <Route path="/contact" element={
        <RouteErrorBoundary routeName="Contact">
          <UnderConstruction />
        </RouteErrorBoundary>
      } />
      
      {/* Authentication routes */}
      <Route path="/auth" element={
        <RouteErrorBoundary routeName="Authentication">
          <Auth />
        </RouteErrorBoundary>
      } />
      <Route path="/auth/callback" element={
        <RouteErrorBoundary routeName="Auth Callback">
          <AuthCallback />
        </RouteErrorBoundary>
      } />
      
      {/* Content creation routes */}
      <Route path="/tutorial-creator" element={
        <RouteErrorBoundary routeName="Tutorial Creator">
          <TutorialCreator />
        </RouteErrorBoundary>
      } />
      <Route path="/contribute" element={
        <RouteErrorBoundary routeName="Contribute">
          <Contribute />
        </RouteErrorBoundary>
      } />
      <Route path="/suggest-feature" element={
        <RouteErrorBoundary routeName="Suggest Feature">
          <SuggestFeature />
        </RouteErrorBoundary>
      } />
      
      {/* Information routes - Protected by PageGuard */}
      <Route path="/roadmap" element={
        <RouteErrorBoundary routeName="Roadmap">
          <PageGuard pageName="roadmap">
            <Roadmap />
          </PageGuard>
        </RouteErrorBoundary>
      } />
      
      {/* Profile routes */}
      <Route path="/profile" element={
        <RouteErrorBoundary routeName="Profile">
          <ProfilePage />
        </RouteErrorBoundary>
      } />
      <Route path="/profile/:username" element={
        <RouteErrorBoundary routeName="Profile">
          <ProfilePage />
        </RouteErrorBoundary>
      } />
      
      {/* Preferences routes - renamed from settings/preferences */}
      <Route path="/preferences" element={
        <RouteErrorBoundary routeName="Preferences">
          <PreferencesPage />
        </RouteErrorBoundary>
      } />
      
      {/* Legacy redirects for backward compatibility */}
      <Route path="/settings" element={<Navigate to="/preferences" replace />} />
      
      <Route path="/saved" element={
        <RouteErrorBoundary routeName="Saved Items">
          <SavedItems />
        </RouteErrorBoundary>
      } />
      
      {/* Admin routes - protected */}
      <Route path="/admin" element={
        <AdminRoute>
          <Navigate to="/admin/dashboard" replace />
        </AdminRoute>
      } />
      <Route path="/admin/dashboard" element={
        <AdminRoute>
          <RouteErrorBoundary routeName="Admin Dashboard">
            <AdminDashboard />
          </RouteErrorBoundary>
        </AdminRoute>
      } />
      <Route path="/admin/submissions" element={
        <AdminRoute>
          <RouteErrorBoundary routeName="Admin Submissions">
            <AdminSubmissions />
          </RouteErrorBoundary>
        </AdminRoute>
      } />
      <Route path="/admin/management" element={
        <AdminRoute>
          <RouteErrorBoundary routeName="Admin Management">
            <FilamentManagement />
          </RouteErrorBoundary>
        </AdminRoute>
      } />
      <Route path="/admin/analytics" element={
        <AdminRoute>
          <RouteErrorBoundary routeName="Admin Analytics">
            <AdminAnalytics />
          </RouteErrorBoundary>
        </AdminRoute>
      } />
      <Route path="/admin/components" element={
        <AdminRoute>
          <RouteErrorBoundary routeName="Component Manager">
            <ComponentManager />
          </RouteErrorBoundary>
        </AdminRoute>
      } />
      <Route path="/admin/access-control" element={
        <AdminRoute>
          <RouteErrorBoundary routeName="Page Access Control">
            <PageAccessControl />
          </RouteErrorBoundary>
        </AdminRoute>
      } />
      
      {/* Redirect old routes to new ones */}
      <Route path="/admin/filaments" element={
        <AdminRoute>
          <Navigate to="/admin/management" replace />
        </AdminRoute>
      } />
      <Route path="/admin/manufacturer-printer-management" element={
        <AdminRoute>
          <Navigate to="/admin/dashboard" replace />
        </AdminRoute>
      } />
      
      {/* 404 fallback route - must be last */}
      <Route path="*" element={
        <RouteErrorBoundary routeName="Not Found">
          <NotFound />
        </RouteErrorBoundary>
      } />
    </Routes>
  );
};

export default RouteConfig;
